import React, { useCallback, useEffect, useState } from 'react';
import { albumApi, engagementApi, leadApi } from '../services/api';
import { AlbumFrame, LeadRow, SurfaceReport } from '../types';
import { istToday, pct } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import { Section, Dash, Failed, Loading, Note, Out, PageFrame, Strip, Th, td, tdBig, tdOurs, tr } from '../components/PlatformBits';
import { Cuts, SourcesTable, SurfaceLeads, n, prettyIntent, surfaceBadge } from '../components/SurfaceBits';

/** The album as an engagement surface: opened → watched → asked → tapped.
 *  Most openers are leads we sent it to, so its job is holding attention and
 *  prompting the next step, not acquisition. Frame captions come from the
 *  album catalog at render time; the portal names no frame. */

const OUR_SENDS = new Set(['welcome', 'campaign', 'share', 'manual', 'album']);

const AlbumPage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [data, setData] = useState<SurfaceReport | null>(null);
  const [names, setNames] = useState<Map<string, LeadRow>>(new Map());
  const [catalog, setCatalog] = useState<Map<string, AlbumFrame>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setData(null); setError(null);
    engagementApi.surface('album', range.from, range.to).then((r) => {
      setData(r);
      return leadApi.byIds(r.leads.map((l) => l.lead_id)).then((rows) => setNames(new Map(rows.map((l) => [l.id, l]))));
    }).catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { albumApi.frames().then((fr) => setCatalog(new Map(fr.map((f) => [f.code, f])))).catch(() => undefined); }, []);

  if (error) return <Failed what="the album" error={error} />;

  const f = data?.funnel ?? {};
  const strangers = (f.visitors ?? 0) - (f.known_leads ?? 0);
  // Visits first (§0): the album's job is to move a lead one step, and
  // stages move on a call — so the visit count leads the strip.
  const strip: StripItem[] = data ? [
    { v: n(f.leads_scheduled), k: 'openers with a visit scheduled', cls: 'visited' },
    { v: n(f.new_leads), k: 'strangers became leads', p: strangers ? `of ${n(strangers)}` : undefined, cls: 'gold' },
    { v: n(f.visitors), k: 'opened', p: `${n(f.known_leads)} leads · ${n(strangers)} strangers` },
    { v: n(f.watched), k: 'watched a frame', p: `${pct(f.watched ?? 0, f.visitors ?? 0)}${f.frames_each ? ` · ${f.frames_each} each` : ''}` },
    { v: n(f.asked), k: 'asked', p: `${n(f.answered)} answered · ${n(f.dismissed)} dismissed` },
    { v: n(f.tapped), k: 'tapped a button' },
  ] : [];

  // Our sends first (welcome, drips, shares, operator), then ads and organic.
  const sources = [...(data?.sources ?? [])].sort((a, b) =>
    Number(OUR_SENDS.has(b.source_kind)) - Number(OUR_SENDS.has(a.source_kind)) || b.visitors - a.visitors);
  const caption = (code: string) => catalog.get(code)?.title ?? code;
  const maxHeld = Math.max(1, ...(data?.frames ?? []).map((fr) => fr.held_for_s ?? 0));

  return (
    <PageFrame
      title="Album"
      badge={surfaceBadge('album')}
      subline={<>bhuvanafortunegreens.com/album{catalog.size ? ` · ${catalog.size} frames` : ''} · <Out href="https://bhuvanafortunegreens.com/album">Open the album</Out> · <Out href="https://marketing.bhuvanafortunegreens.com/album">Curate frames</Out></>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!data && <Loading what="the album" />}

      {data && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <SourcesTable sources={sources} album />

          {data.frames && (
            <Section title="Frames">
              <table className="w-full min-w-[860px] border-collapse">
                <thead><tr><Th left>Frame</Th><Th>Viewers</Th><Th>Watched 10 s+</Th><Th>Held</Th><Th>Liked</Th><Th>Shared</Th><Th ours>Tapped after</Th></tr></thead>
                <tbody>
                  {data.frames.map((fr) => (
                    <tr key={fr.frame} className={`${tr} [&>td]:align-middle`}>
                      <td className="px-3 py-2.5">
                        <span className="flex items-center gap-3">
                          {catalog.get(fr.frame)?.poster_url
                            ? <img src={catalog.get(fr.frame)!.poster_url!} alt="" className="w-10 h-10 rounded-md object-cover flex-none" />
                            : <span className="w-10 h-10 rounded-md bg-charcoal/[0.05] flex-none" />}
                          <span className="min-w-0">
                            <span className="block font-semibold">{caption(fr.frame)}</span>
                            <span className="block text-[11px] text-graphite/80 font-mono">{fr.frame}</span>
                          </span>
                        </span>
                      </td>
                      <td className={tdBig}>{n(fr.viewers)}</td>
                      <td className={`${td} text-right`}>{fr.watched ? <>{n(fr.watched)}<span className="text-graphite text-[11.5px] ml-1.5">{pct(fr.watched, fr.viewers)}</span></> : <Dash />}</td>
                      <td className={`${td} text-right`}>
                        {fr.held_for_s != null ? (
                          <span className="inline-flex items-center justify-end gap-2">
                            <i className={`block h-1.5 rounded-full ${fr.held_for_s >= 10 ? 'bg-forest' : fr.held_for_s < 3 ? 'bg-brick' : 'bg-sage'}`}
                               style={{ width: `${Math.round(fr.held_for_s / maxHeld * 120)}px` }} />
                            <span className="font-semibold w-12 text-right">{fr.held_for_s.toFixed(1)} s</span>
                          </span>
                        ) : <Dash />}
                      </td>
                      <td className={`${td} text-right`}>{fr.liked || <Dash />}</td>
                      <td className={`${td} text-right`}>{fr.shared || <Dash />}</td>
                      <td className={tdOurs}>{fr.tapped_after || <Dash />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Note><b className="text-charcoal">Held</b> — median seconds a viewer stayed on the frame. <b className="text-charcoal">Tapped after</b> — viewers whose next act was a button.</Note>
            </Section>
          )}

          {data.asks && data.asks.length > 0 && (
            <Section title="The ask" note="shown to engaged viewers">
              <table className="w-full min-w-[640px] border-collapse">
                <thead><tr><Th left>Ask</Th><Th>Shown</Th><Th ours>Answered</Th><Th>Dismissed</Th><Th ours>Led to a tap</Th></tr></thead>
                <tbody>
                  {data.asks.map((a) => (
                    <tr key={a.ask} className={tr}>
                      <td className="px-3 py-2.5 font-semibold">{a.ask}</td>
                      <td className={tdBig}>{n(a.shown)}</td>
                      <td className={tdOurs}>{a.answered || <Dash />}</td>
                      <td className={`${td} text-right`}>{a.dismissed || <Dash />}</td>
                      <td className={`${td} text-right`}>{a.led_to_tap || <Dash />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          <Cuts cuts={data.cuts} verb="Opened" />

          <SurfaceLeads
            title="Leads who opened the album"
            leads={data.leads}
            names={names}
            extraHeaders={['Frames', 'Longest', 'Tapped']}
            extra={(l) => [
              <span className="num">{l.frames || <Dash />}</span>,
              l.longest_frame ? `${caption(l.longest_frame)} · ${Math.round(l.longest_s ?? 0)} s` : <Dash />,
              l.tap_intent ? prettyIntent(l.tap_intent) : <Dash />,
            ]}
          />
        </>
      )}
    </PageFrame>
  );
};

export default AlbumPage;
